import { seoFixturesEnabled, fixtureEvent } from "../fixtures/seoFixtures";
import { supabase } from "../admin/supabase";

/**
 * Service layer for the public Recent Events module. Extends the
 * existing `events` table (already backing /admin/events,
 * RecentEvents.jsx and UpcomingEvents.jsx) plus the child tables added
 * by 20260801100000_events_module.sql (Phase 1: discovery experience)
 * and 20260801110000_events_phase2_registration_analytics.sql (Phase 2:
 * registration + analytics — see registrationService.js and
 * analyticsService.js for those).
 *
 * No component should query Supabase directly for event data —
 * everything the events pages need goes through the functions here.
 */

const EVENT_LIST_COLUMNS = `
  id, slug, title, description, body, featured_image, category_id, location,
  campus, department, school, organizer, google_maps, start_at, end_at,
  event_type, event_mode, meeting_link, registration_deadline, capacity,
  registered_count, available_seats, register_url, registration_mode,
  is_free, price, event_status_override, timezone, is_featured, published,
  view_count, status, location_id,
  seo_title, seo_description, seo_keywords, canonical_url, og_image,
  created_at, updated_at,
  category:event_categories ( id, name, slug ),
  event_location:event_locations ( id, name, address, campus, google_maps, capacity ),
  event_tag_links ( tags ( id, name, slug ) )
`;

const EVENT_DETAIL_COLUMNS = `
  ${EVENT_LIST_COLUMNS},
  event_speakers ( id, full_name, title, organization, bio, photo_url, linkedin_url, website_url, twitter_url, session_time, topic, display_order ),
  event_sessions ( id, start_time, end_time, title, description, speaker_name, display_order ),
  event_gallery ( id, media_type, url, caption, display_order ),
  event_downloads ( id, title, file_url, file_type, display_order ),
  event_sponsors ( id, kind, name, logo_url, tier, website_url, display_order )
`;

/** Flattens the nested embed shapes into plain arrays, and splits sponsors from partners. */
function normalizeEvent(row) {
  if (!row) return null;

  const tags = (row.event_tag_links || []).map((link) => link.tags).filter(Boolean);
  const { event_tag_links, event_sponsors, event_location, ...rest } = row;

  const normalized = { ...rest, tags, locationDetail: event_location || null };

  if (event_sponsors) {
    normalized.sponsors = event_sponsors.filter((s) => s.kind === "sponsor");
    normalized.partners = event_sponsors.filter((s) => s.kind === "partner");
  }
  if (normalized.event_speakers) {
    normalized.speakers = [...normalized.event_speakers].sort((a, b) => a.display_order - b.display_order);
    delete normalized.event_speakers;
  }
  if (normalized.event_sessions) {
    normalized.sessions = [...normalized.event_sessions].sort((a, b) => a.display_order - b.display_order);
    delete normalized.event_sessions;
  }
  if (normalized.event_gallery) {
    normalized.gallery = [...normalized.event_gallery].sort((a, b) => a.display_order - b.display_order);
    delete normalized.event_gallery;
  }
  if (normalized.event_downloads) {
    normalized.downloads = [...normalized.event_downloads].sort((a, b) => a.display_order - b.display_order);
    delete normalized.event_downloads;
  }

  return normalized;
}

/**
 * Fetch a paginated, filterable list of published events.
 * filters: { categorySlug, tagSlug, mode, isFree, campus, when, page = 1, pageSize = 9 }
 * `when` is one of "upcoming" | "ongoing" | "past" | undefined (all).
 * Returns { events, total, page, pageSize, totalPages }.
 */
export async function getEvents({
  categorySlug,
  tagSlug,
  mode,
  isFree,
  campus,
  when,
  page = 1,
  pageSize = 9,
} = {}) {
  let query = supabase
    .from("events")
    .select(EVENT_LIST_COLUMNS, { count: "exact" })
    .eq("published", true)
    // Recent Events is a chronological archive: the newest item belongs on
    // page 1 and pagination then moves backwards through time. Display order
    // only settles entries that have the same event date.
    .order("start_at", { ascending: false })
    .order("display_order", { ascending: false });

  if (categorySlug) {
    const { data: cat } = await supabase.from("event_categories").select("id").eq("slug", categorySlug).maybeSingle();
    query = query.eq("category_id", cat?.id || "00000000-0000-0000-0000-000000000000");
  }

  if (tagSlug) {
    const { data: tag } = await supabase.from("tags").select("id").eq("slug", tagSlug).maybeSingle();
    if (tag?.id) {
      const { data: links } = await supabase.from("event_tag_links").select("event_id").eq("tag_id", tag.id);
      const ids = (links || []).map((l) => l.event_id);
      query = query.in("id", ids.length ? ids : ["00000000-0000-0000-0000-000000000000"]);
    }
  }

  if (mode) query = query.eq("event_mode", mode);
  if (isFree === true) query = query.eq("is_free", true);
  if (isFree === false) query = query.eq("is_free", false);
  if (campus) query = query.eq("campus", campus);

  const now = new Date().toISOString();
  if (when === "upcoming") query = query.gt("start_at", now);
  else if (when === "past") query = query.lt("end_at", now);
  else if (when === "ongoing") query = query.lte("start_at", now).gte("end_at", now);

  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;
  query = query.range(from, to);

  const { data, error, count } = await query;
  if (error) throw error;

  return {
    events: (data || []).map(normalizeEvent),
    total: count || 0,
    page,
    pageSize,
    totalPages: Math.max(1, Math.ceil((count || 0) / pageSize)),
  };
}

/** Fetch a single published event by its SEO-friendly slug, with every child relation for the detail page. */
export async function getEventBySlug(slug) {
  const { data, error } = await supabase
    .from("events")
    .select(EVENT_DETAIL_COLUMNS)
    .eq("slug", slug)
    .eq("published", true)
    .order("display_order", { foreignTable: "event_speakers", ascending: true })
    .order("display_order", { foreignTable: "event_sessions", ascending: true })
    .order("display_order", { foreignTable: "event_gallery", ascending: true })
    .order("display_order", { foreignTable: "event_downloads", ascending: true })
    .order("display_order", { foreignTable: "event_sponsors", ascending: true })
    .maybeSingle();

  if (error && !seoFixturesEnabled()) throw error;
  const event = normalizeEvent(data);
  if (!event && seoFixturesEnabled()) return fixtureEvent(slug);
  return event;
}

/** The event flagged `is_featured`, soonest-starting first, for the events hero. Falls back to the next upcoming event. */
export async function getFeaturedEvent() {
  const now = new Date().toISOString();

  const { data: featured, error: featuredError } = await supabase
    .from("events")
    .select(EVENT_LIST_COLUMNS)
    .eq("published", true)
    .eq("is_featured", true)
    .gte("end_at", now)
    .order("start_at", { ascending: true })
    .limit(1)
    .maybeSingle();
  if (featuredError) throw featuredError;
  if (featured) return normalizeEvent(featured);

  const { data: next, error: nextError } = await supabase
    .from("events")
    .select(EVENT_LIST_COLUMNS)
    .eq("published", true)
    .gte("end_at", now)
    .order("start_at", { ascending: true })
    .limit(1)
    .maybeSingle();
  if (nextError) throw nextError;
  return normalizeEvent(next);
}

/** Upcoming (not yet ended) published events, soonest first — homepage strips and "This Week" widgets. */
export async function getUpcomingEvents(limit = 6) {
  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from("events")
    .select(EVENT_LIST_COLUMNS)
    .eq("published", true)
    .gte("end_at", now)
    .order("display_order", { ascending: false })
    .order("start_at", { ascending: true })
    .limit(limit);

  if (error) throw error;
  return (data || []).map(normalizeEvent);
}

/** Most recent past published events, for "Recent Events" strips. */
export async function getRecentPastEvents(limit = 6) {
  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from("events")
    .select(EVENT_LIST_COLUMNS)
    .eq("published", true)
    .lt("end_at", now)
    .order("display_order", { ascending: false })
    .order("start_at", { ascending: false })
    .limit(limit);

  if (error) throw error;
  return (data || []).map(normalizeEvent);
}

/**
 * Related events for a given event: same category OR overlapping tags
 * OR same event type, excluding the current event. Returns up to
 * `limit` results (3–6 recommended by the design spec).
 */
export async function getRelatedEvents(event, limit = 4) {
  if (!event) return [];

  const tagIds = (event.tags || []).map((t) => t.id);
  let tagEventIds = [];
  if (tagIds.length) {
    const { data: links } = await supabase
      .from("event_tag_links")
      .select("event_id")
      .in("tag_id", tagIds)
      .neq("event_id", event.id);
    tagEventIds = (links || []).map((l) => l.event_id);
  }

  const orParts = [];
  if (event.category_id) orParts.push(`category_id.eq.${event.category_id}`);
  if (tagEventIds.length) orParts.push(`id.in.(${tagEventIds.join(",")})`);
  if (event.event_type) orParts.push(`event_type.eq.${event.event_type}`);

  if (!orParts.length) return [];

  const { data, error } = await supabase
    .from("events")
    .select(EVENT_LIST_COLUMNS)
    .eq("published", true)
    .neq("id", event.id)
    .or(orParts.join(","))
    .order("start_at", { ascending: true })
    .limit(limit);

  if (error) throw error;
  return (data || []).map(normalizeEvent);
}

/** All tags currently used on events, alphabetically — reuses the blog module's shared `tags` table. */
export async function getEventTags() {
  const { data, error } = await supabase.from("tags").select("id, name, slug").order("name", { ascending: true });
  if (error) throw error;
  return data || [];
}

/** Distinct campuses currently in use, for the campus filter dropdown. */
export async function getEventCampuses() {
  const { data, error } = await supabase
    .from("events")
    .select("campus")
    .eq("published", true)
    .not("campus", "is", null);
  if (error) throw error;
  return Array.from(new Set((data || []).map((r) => r.campus).filter(Boolean))).sort();
}

/**
 * Full-text + category/tag-name search across published events, via the
 * search_events() Postgres function (single round trip for ranking).
 */
export async function searchEvents(term, limit = 20) {
  const query = (term || "").trim().toLocaleLowerCase();
  if (!query) return [];

  const { data: matches, error } = await supabase.rpc("search_events", {
    search_query: query,
    result_limit: limit,
  });
  if (error) throw error;

  const ids = (matches || []).map((m) => m.id);
  if (!ids.length) return [];

  const { data, error: eventsError } = await supabase.from("events").select(EVENT_LIST_COLUMNS).in("id", ids);
  if (eventsError) throw eventsError;

  const byId = new Map((data || []).map((row) => [row.id, normalizeEvent(row)]));
  // Preserve the relevance ranking returned by search_events().
  return ids.map((id) => byId.get(id)).filter(Boolean);
}
